'use client';

import type { JSX } from 'react';
import * as prismicH from '@prismicio/helpers';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';

import { Products } from '@/kiosk/_components/Products';
import { SORT_BY_MAP, stringToSlug } from '@/utils';
import { EnumCustomerType, SortBySelectOption, TreezStore } from '@/types';
import { useShowByLocationSlug } from '@/hooks';

export type KioskProductsCarouselProps =
  SliceComponentProps<Content.KioskProductsCarouselSlice>;

const KioskProductsCarousel = ({
  slice,
}: KioskProductsCarouselProps): JSX.Element | null => {
  const {
    see_all_link,
    see_all_text,
    title,
    limit,
    sort_by,
    type,
    filter_by_brand,
    product_group_id,
    filter_by_on_sale,
    filter_by_store,
    bottom_spacing,
    padding_section,
    location_slug,
    background_color,
    background_image,
    background_style_fixed_cover,
    min_heigth,
    title_color,
    see_all_text_color,
    active_control_color,
    disable_control_color,
    effects,
    flavors,
    generals,
  } = slice.primary;

  const { showContent } = useShowByLocationSlug(location_slug as string);

  if (!slice?.primary?.active || !showContent) return null;

  const carouselListTitle = prismicH.asText(title) ?? 'Products';

  const analyticsListName = stringToSlug(carouselListTitle);

  const selectedStore = (slice as any).selectedStore as TreezStore;
  const selectedCustomerType = (slice as any)
    .selectedCustomerType as EnumCustomerType;

  const hasLocationSlug = prismicH.isFilled.keyText(location_slug);
  const hasType = prismicH.isFilled.select(type);
  const hasFilterByBrand = prismicH.isFilled.keyText(filter_by_brand);
  const hasFilterByGroupId = prismicH.isFilled.keyText(product_group_id);
  const hasFilterByOnSale = filter_by_on_sale;
  const hasEffects = prismicH.isFilled.keyText(effects);
  const hasFlavors = prismicH.isFilled.keyText(flavors);
  const hasGenerals = prismicH.isFilled.keyText(generals);

  if (
    !hasLocationSlug &&
    !hasType &&
    !hasFilterByBrand &&
    !hasFilterByGroupId &&
    !hasFilterByOnSale &&
    !hasEffects &&
    !hasFlavors &&
    !hasGenerals
  ) {
    return null;
  }

  return (
    <Products
      activeControlColor={active_control_color}
      analyticsListName={analyticsListName}
      backgroundColor={background_color}
      backgroundImage={background_image}
      backgroundStyleFixed={background_style_fixed_cover}
      disableControlColor={disable_control_color}
      effects={effects?.toString()}
      filterByBrand={filter_by_brand!}
      filterByCategory={type!}
      filterByGroupId={product_group_id!}
      filterByOnSale={filter_by_on_sale}
      filterByStore={filter_by_store}
      flavors={flavors?.toString()}
      generals={generals?.toString()}
      key={selectedStore?.short_name}
      limit={limit!}
      locationSlug={location_slug!}
      minHeight={min_heigth!}
      paddingSection={padding_section}
      products={(slice as any).products}
      seeAllHref={see_all_link ?? '/'}
      seeAllTextColor={see_all_text_color}
      seeMoreText={see_all_text!}
      selectedCustomerType={selectedCustomerType}
      selectedStore={selectedStore}
      sortBy={SORT_BY_MAP?.[sort_by] as SortBySelectOption}
      spacingBottom={bottom_spacing}
      title={carouselListTitle}
      titleColor={title_color}
      totalCount={(slice as any).totalCount}
    />
  );
};

export default KioskProductsCarousel;
